import { Home, Moon, Sun, Minimize, Shield, GraduationCap, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import AuthButton from "./AuthButton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useProfile } from "@/hooks/use-profile";
import { useRolePerspective, type AppPerspective } from "@/hooks/use-role-perspective";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSubject } from "@/contexts/SubjectContext";

interface HeaderProps {
  onHomeClick?: () => void;
  showHome?: boolean;
  isFullscreen?: boolean;
  onExitFullscreen?: () => void;
}

export default function Header({ onHomeClick, showHome = true, isFullscreen = false, onExitFullscreen }: HeaderProps) {
  const [isDark, setIsDark] = useState(false);
  const [, setLocation] = useLocation();
  const [isInFullscreen, setIsInFullscreen] = useState(false);
  const { user, profile, loading } = useProfile();
  const { perspective, setPerspective, isAdminAccount } = useRolePerspective(user?.id, profile?.role);
  const { currentSubject, setCurrentSubject, subjects } = useSubject();

  useEffect(() => {
    const darkMode = localStorage.getItem("darkMode") === "true";
    setIsDark(darkMode);
    if (darkMode) {
      document.documentElement.classList.add("dark");
    }

    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      setIsInFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleExitFullscreen = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    }
    if (onExitFullscreen) {
      onExitFullscreen();
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDark;
    setIsDark(newDarkMode);
    localStorage.setItem("darkMode", String(newDarkMode));
    if (newDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Link href="/">
            <div className="flex items-center gap-1 cursor-pointer">
              <span className="text-2xl font-bold text-primary">+</span>
              <h1 className="font-serif text-xl font-bold hover:opacity-80 transition-opacity">
                <span className="text-primary">Add</span>
                <span className="text-foreground">vance</span>
              </h1>
            </div>
          </Link>

          <div className="flex items-center">
            {currentSubject && (
              <Select 
                value={currentSubject.id} 
                onValueChange={(val) => {
                  const subject = subjects.find(s => s.id === val);
                  if (subject) setCurrentSubject(subject);
                }}
              >
                <SelectTrigger className="h-8 border-transparent bg-transparent hover:bg-muted/50 text-primary font-serif font-bold text-xl px-2 shadow-none focus:ring-0 [&>svg]:ml-1">
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((subject) => (
                    <SelectItem key={subject.id} value={subject.id} className="font-serif text-lg font-bold">
                      {subject.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <span className="text-sm text-muted-foreground hidden lg:inline ml-2 border-l pl-3">AddvanceLab</span>
          <span className="text-[10px] text-muted-foreground/50 hidden lg:inline" title={`Built: ${__BUILD_TIME__}`}>
            v{__BUILD_HASH__}
          </span>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {!loading && isAdminAccount && (
            <div className="flex items-center gap-2 rounded-full border border-border/70 bg-background/75 px-1.5 py-1 shadow-sm shadow-black/5 backdrop-blur-md transition-all duration-300 ease-out">
              <span className="hidden xl:inline px-2 text-[10px] font-semibold uppercase tracking-[0.26em] text-muted-foreground">
                Perspective
              </span>
              <ToggleGroup
                type="single"
                value={perspective ?? "admin"}
                onValueChange={(value) => {
                  if (value) {
                    setPerspective(value as AppPerspective);
                  }
                }}
                variant="default"
                size="sm"
                className="gap-1 rounded-full bg-muted/40 p-1 ring-1 ring-inset ring-border/60 transition-all duration-300 ease-out"
                aria-label="Preview perspective"
              >
                <ToggleGroupItem value="admin" aria-label="Admin perspective" className="gap-1.5 rounded-full px-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground transition-all duration-300 ease-out data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md data-[state=on]:scale-[1.03] hover:text-foreground">
                  <Shield className="h-3.5 w-3.5" />
                  Admin
                </ToggleGroupItem>
                <ToggleGroupItem value="teacher" aria-label="Teacher perspective" className="gap-1.5 rounded-full px-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground transition-all duration-300 ease-out data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md data-[state=on]:scale-[1.03] hover:text-foreground">
                  <GraduationCap className="h-3.5 w-3.5" />
                  Teacher
                </ToggleGroupItem>
                <ToggleGroupItem value="student" aria-label="Student perspective" className="gap-1.5 rounded-full px-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground transition-all duration-300 ease-out data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md data-[state=on]:scale-[1.03] hover:text-foreground">
                  <UserRound className="h-3.5 w-3.5" />
                  Student
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}
          

          
          <AuthButton />
          {isInFullscreen && (
            <Button
              variant="default"
              size="icon"
              onClick={handleExitFullscreen}
              title="Exit Fullscreen (ESC)"
            >
              <Minimize className="h-5 w-5" />
            </Button>
          )}
          {showHome && (
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => {
                if (isInFullscreen) {
                  await handleExitFullscreen();
                }
                onHomeClick?.();
              }}
              data-testid="button-home"
            >
              <Home className="h-5 w-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDarkMode}
            data-testid="button-theme-toggle"
          >
            {isDark ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>
    </header>
  );
}
